import type { MessageId } from "@redotech/effect-api-shared/ids/definitions";
import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { Attachment } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/attachment";
import { useMemo } from "react";

export interface FormattedChatMessage {
  id: MessageId;
  content: string;
  isFromCustomer: boolean;
  timestamp: Date;
  formattedTime: string;
  attachments: readonly Attachment[];
}

export interface ChatMessageGroup {
  dateLabel: string;
  messages: FormattedChatMessage[];
}

export interface ChatThreadViewState {
  messageGroups: ChatMessageGroup[];
  lastMessageId: MessageId | null;
}

export function useChatThreadView(messages: Message[]): ChatThreadViewState {
  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.sentAt.getTime() - b.sentAt.getTime()),
    [messages],
  );

  const lastMessageId = useMemo(() => {
    if (sortedMessages.length === 0) {
      return null;
    }
    return sortedMessages[sortedMessages.length - 1]?.id ?? null;
  }, [sortedMessages]);

  const messageGroups = useMemo(
    () => groupMessagesByDate(sortedMessages),
    [sortedMessages],
  );

  return { messageGroups, lastMessageId };
}

function groupMessagesByDate(messages: Message[]): ChatMessageGroup[] {
  const groups: ChatMessageGroup[] = [];
  let currentDateLabel: string | null = null;
  let currentMessages: FormattedChatMessage[] = [];

  for (const message of messages) {
    const dateLabel = formatChatDate(message.sentAt);

    if (dateLabel !== currentDateLabel) {
      if (currentDateLabel !== null && currentMessages.length > 0) {
        groups.push({ dateLabel: currentDateLabel, messages: currentMessages });
      }
      currentDateLabel = dateLabel;
      currentMessages = [];
    }

    currentMessages.push(formatChatMessage(message));
  }

  if (currentDateLabel !== null && currentMessages.length > 0) {
    groups.push({ dateLabel: currentDateLabel, messages: currentMessages });
  }

  return groups;
}

function formatChatMessage(message: Message): FormattedChatMessage {
  return {
    id: message.id,
    content: message.content ?? "",
    isFromCustomer: message.origin === "external",
    timestamp: message.sentAt,
    formattedTime: formatChatTime(message.sentAt),
    attachments: message.attachments,
  };
}

function formatChatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatChatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}
